import { avatarSrc } from "@/lib/defaults";
import { pausableInterval } from "@/lib/appVisibility";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { whenFeedReady } from "@/lib/lowPriority";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { formatLastSeen } from "@/lib/lastSeen";

// Facebook/Messenger-style flat contacts list (people I follow OR who follow me) — click
// opens the chat dock. Merging both directions keeps presence RECIPROCAL: if someone
// added you (even if you don't follow back) they appear here and you see them online.
// Shows REAL presence (green dot + "En ligne"), a "Vu il y a X" label when offline,
// a live "écrit…" indicator, an online-friends counter, and unread message badges.
export const ContactsWidget = () => {
  const { user, openChat, t, lang } = useApp();
  const [contacts, setContacts] = useState([]);
  const [presence, setPresence] = useState({});
  const [typing, setTyping] = useState({});
  const [unread, setUnread] = useState({});
  const typingTimers = useRef({});

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const cancel = whenFeedReady(() => Promise.all([
      api.get(`/users/${user.id}/following`).catch(() => ({ data: {} })),
      api.get(`/users/${user.id}/followers`).catch(() => ({ data: {} })),
    ]).then(([f1, f2]) => {
      if (!alive) return;
      const map = new Map();
      for (const u of [...(f1.data.users || []), ...(f2.data.users || [])]) {
        if (u?.id && u.id !== user.id && !map.has(u.id)) map.set(u.id, u);
      }
      setContacts([...map.values()]);
    }).catch(() => {}));
    return () => { alive = false; cancel(); };
  }, [user]);

  // Poll presence for a wider pool (API cap = 50) so an online friend anywhere in
  // your follow list still surfaces — before, presence was only fetched for the first
  // 12 rows, so an online friend past position 12 looked offline (asymmetric presence).
  const presencePool = useMemo(() => contacts.slice(0, 50), [contacts]);

  // Poll real online presence for the pool (kept fresh every 45s).
  useEffect(() => {
    const ids = presencePool.map((u) => u.id);
    if (!user || ids.length === 0) return;
    let alive = true;
    const load = () => api.get(`/presence?ids=${ids.join(",")}`)
      .then(({ data }) => { if (alive) setPresence(data.presence || {}); })
      .catch(() => {});
    load();
    const stop = pausableInterval(load, 45000); // lot 699 — repos en arrière-plan
    return () => { alive = false; stop(); };
  }, [user, presencePool]);

  // Unread message counts per contact (from conversations), refreshed on chat activity.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = () => api.get("/chat/conversations")
      .then(({ data }) => {
        if (!alive) return;
        const m = {};
        for (const c of data.conversations || []) {
          if (c.other?.id && (c.unread || 0) > 0) m[c.other.id] = c.unread;
        }
        setUnread(m);
      })
      .catch(() => {});
    const cancelFirst = whenFeedReady(load);
    const on = () => load();
    window.addEventListener("yalola:chat", on);
    window.addEventListener("yalola:read", on);
    return () => { alive = false; cancelFirst(); window.removeEventListener("yalola:chat", on); window.removeEventListener("yalola:read", on); };
  }, [user]);

  // Live "écrit…" — the global chat socket (ChatDock) broadcasts `yalola:typing`
  // with detail.from = the sender's id whenever a contact types to you.
  useEffect(() => {
    const timers = typingTimers.current;
    const onTyping = (e) => {
      const from = e?.detail?.from;
      if (!from) return;
      setTyping((prev) => ({ ...prev, [from]: true }));
      clearTimeout(timers[from]);
      timers[from] = setTimeout(() => setTyping((prev) => { const n = { ...prev }; delete n[from]; return n; }), 4000);
    };
    window.addEventListener("yalola:typing", onTyping);
    return () => {
      window.removeEventListener("yalola:typing", onTyping);
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  // Active = really online OR currently typing — used only for list ordering.
  const isActive = (u) => !!presence[u.id]?.online || !!typing[u.id];

  // Active contacts float to the top (Messenger-style) across the WHOLE follow list,
  // then we show the top rows — so an online friend anywhere in the list rises into view.
  const ordered = useMemo(
    () => contacts.map((u, i) => [u, i]).sort(
      (a, b) => ((isActive(b[0]) ? 1 : 0) - (isActive(a[0]) ? 1 : 0)) || (a[1] - b[1])
    ).map(([u]) => u),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contacts, presence, typing]
  );
  const shown = useMemo(() => ordered.slice(0, 12), [ordered]);

  const onlineCount = useMemo(() => presencePool.filter((u) => presence[u.id]?.online).length, [presencePool, presence]);

  if (!user || contacts.length === 0) return null;

  const onlineLabel = onlineCount === 1
    ? t("contacts.online_one", "1 ami en ligne")
    : t("contacts.online_many", "{n} amis en ligne").replace("{n}", onlineCount);

  return (
    <div data-testid="contacts-widget">
      <div className="flex items-center justify-between px-2 mb-1">
        <div className="text-[13px] font-bold" style={{ color: "var(--yalola-text-muted)" }}>{t("contacts.title", "Contacts")}</div>
        {onlineCount > 0 && (
          <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: "#16a34a" }} data-testid="contacts-online-count">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            {onlineLabel}
          </div>
        )}
      </div>
      <div>
        {shown.map((u) => {
          const realOnline = !!presence[u.id]?.online; // green dot = REAL presence only
          const isTyping = !!typing[u.id];
          const lastSeen = presence[u.id]?.last_seen;
          const nUnread = unread[u.id] || 0;
          let subtitle = null;
          let subtitleColor = "var(--yalola-text-muted)";
          if (isTyping) {
            subtitle = t("chat.typing", "écrit…");
            subtitleColor = "#16a34a";
          } else if (realOnline) {
            subtitle = t("chat.active", "En ligne");
            subtitleColor = "#16a34a";
          } else if (lastSeen) {
            subtitle = formatLastSeen(lastSeen, t, lang);
          }
          return (
            <button
              key={u.id}
              onClick={() => openChat(u)}
              className="w-full flex items-center gap-3 px-2 py-1.5 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)] text-left"
              data-testid={`contact-${u.id}`}
            >
              <div className="relative shrink-0">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={avatarSrc(u)} />
                  <AvatarFallback>{u.name?.[0]}</AvatarFallback>
                </Avatar>
                {realOnline && (
                  <span
                    className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 border-2"
                    style={{ borderColor: "var(--yalola-bg)" }}
                    data-testid={`contact-online-dot-${u.id}`}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-sm truncate flex items-center gap-1 ${nUnread > 0 ? "font-bold" : "font-medium"}`} style={{ color: "var(--yalola-text)" }}>
                  {u.name} <VerifiedBadge user={u} size={12} />
                </div>
                {subtitle && (
                  <div
                    className={`text-[11px] truncate leading-tight ${isTyping ? "italic" : ""}`}
                    style={{ color: subtitleColor }}
                    data-testid={isTyping ? `contact-typing-${u.id}` : `contact-status-${u.id}`}
                  >
                    {subtitle}
                  </div>
                )}
              </div>
              {nUnread > 0 && (
                <span
                  className="min-w-[18px] h-[18px] px-1.5 rounded-full grid place-items-center text-[10px] font-bold text-white shrink-0"
                  style={{ background: "var(--yalola-primary)" }}
                  data-testid={`contact-unread-${u.id}`}
                >
                  {nUnread > 9 ? "9+" : nUnread}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ContactsWidget;
